import {
  Children, cloneElement, forwardRef, isValidElement, MouseEvent,ReactNode, useCallback, useEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';

import styles from '@/components/dropdownMenu/dropdownMenu.module.scss';
import { useThrottle } from '@/hooks/useThrottle';

const VIEWPORT_MARGIN = 8;
const DROPDOWN_MIN_WIDTH = 200;
const DROPDOWN_OFFSET = 4;
const POSITION_UPDATE_THROTTLE_MS = 16;

interface ActionMenuProps {
  disabled?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  'data-testid'?: string;
}

interface ActionMenuItemProps {
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: ( event: MouseEvent<HTMLButtonElement> ) => void;
  'data-testid'?: string;
}

export const ActionMenu = forwardRef<HTMLDivElement, ActionMenuProps>(
  ( { children, icon, disabled = false, 'data-testid': dataTestId }, forwardedRef ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    const [ position, setPosition ] = useState( { top: 0, left: 0 } );
    const menuRef = useRef<HTMLDivElement>( null );
    const buttonRef = useRef<HTMLButtonElement>( null );

    useEffect( () => {
      const handleClickOutside = ( event: globalThis.MouseEvent ) => {
        if (
          menuRef.current &&
          !menuRef.current.contains( event.target as Node ) &&
          buttonRef.current &&
          !buttonRef.current.contains( event.target as Node )
        ) {
          setIsOpen( false );
        }
      };

      if ( isOpen ) {
        document.addEventListener( 'mousedown', handleClickOutside );
        return () => {
          document.removeEventListener( 'mousedown', handleClickOutside );
        };
      }
    }, [ isOpen ] );

    useEffect( () => {
      const handleEscape = ( event: KeyboardEvent ) => {
        if ( event.key === 'Escape' ) {
          setIsOpen( false );
          buttonRef.current?.focus();
        }
      };

      if ( isOpen ) {
        document.addEventListener( 'keydown', handleEscape );
        return () => {
          document.removeEventListener( 'keydown', handleEscape );
        };
      }
    }, [ isOpen ] );

    const updatePosition = useCallback( () => {
      if ( buttonRef.current ) {
        const rect = buttonRef.current.getBoundingClientRect();

        let left = rect.left;

        if ( left > window.innerWidth - DROPDOWN_MIN_WIDTH ) {
          left = rect.right - DROPDOWN_MIN_WIDTH;
        }

        left = Math.max( VIEWPORT_MARGIN, left );

        setPosition( {
          top: rect.bottom + DROPDOWN_OFFSET,
          left: left
        } );
      }
    }, [] );

    const throttledUpdatePosition = useThrottle( updatePosition, POSITION_UPDATE_THROTTLE_MS );

    const handleToggle = () => {
      if ( !disabled ) {
        if ( !isOpen ) {
          updatePosition();
        }
        setIsOpen( !isOpen );
      }
    };

    const handleItemClick = () => {
      setIsOpen( false );
    };

    useEffect( () => {
      if ( isOpen ) {
        updatePosition();

        window.addEventListener( 'scroll', throttledUpdatePosition, true );
        window.addEventListener( 'resize', throttledUpdatePosition );
        return () => {
          window.removeEventListener( 'scroll', throttledUpdatePosition, true );
          window.removeEventListener( 'resize', throttledUpdatePosition );
        };
      }
    }, [ isOpen, updatePosition, throttledUpdatePosition ] );

    const menuContent = isOpen && (
      <div
        ref={ menuRef }
        className={ styles.Menu }
        role="menu"
        style={ {
          position: 'fixed',
          top: `${ position.top }px`,
          left: `${ position.left }px`,
          minWidth: `${ DROPDOWN_MIN_WIDTH }px`
        } }
      >
        {Children.map( children, ( child ) => {
          if ( isValidElement<ActionMenuItemProps>( child ) ) {
            return cloneElement( child, {
              onClick: ( e: MouseEvent<HTMLButtonElement> ) => {
                child.props.onClick?.( e );
                handleItemClick();
              }
            } );
          }
          return child;
        } )}
      </div>
    );

    const buttonId = dataTestId ? `${ dataTestId }-button` : undefined;

    return (
      <div className={ styles.MenuWrapper } ref={ forwardedRef }>
        <button
          ref={ buttonRef }
          id={ buttonId }
          disabled={ disabled }
          className={ styles.RootMenu }
          onClick={ handleToggle }
          data-open={ isOpen ? '' : undefined }
          data-testid={ dataTestId }
          type="button"
          aria-haspopup="true"
          aria-expanded={ isOpen }
        >
          <span className={ styles.ButtonLabel }>{icon}</span>
        </button>
        {typeof document !== 'undefined' && menuContent && createPortal( menuContent, document.body )}
      </div>
    );
  }
);
ActionMenu.displayName = 'ActionMenu';

export const ActionMenuItem = forwardRef<HTMLButtonElement, ActionMenuItemProps>(
  ( { value, icon, disabled, onClick, 'data-testid': dataTestId, ...props }, forwardedRef ) => {
    return (
      <button
        { ...props }
        ref={ forwardedRef }
        type="button"
        role="menuitem"
        className={ styles.MenuItem }
        disabled={ disabled }
        onClick={ onClick }
        data-testid={ dataTestId }
      >
        <div style={ { display: 'flex', alignItems: 'center', gap: '8px' } }>
          {icon && <span style={ { display: 'flex', alignItems: 'center' } }>{icon}</span>}
          <span>{value}</span>
        </div>
      </button>
    );
  }
);
ActionMenuItem.displayName = 'ActionMenuItem';
